import { sum, template } from 'lodash-es'

export function uid() {
  return Math.random().toString(36).substr(2, 10)
}

export function validURL(href) {
  const match = href.match(
    /(http(s)?:\/\/)(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g,
  )
  return match !== null
}

export function normalizeUrl(value) {
  if (!value) {
    return null
  }
  const trimmed = String(value).trim()
  if (trimmed === '') {
    return null
  }
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

export const naturalSort = (a, b) =>
  a
    .toString()
    .localeCompare(b.toString(), 'en', { numeric: true, sensitivity: 'base' })

export const deepCopy = data => JSON.parse(JSON.stringify(data))

export const formatLink = (link, data) => {
  const compiled = template(link)
  return compiled(data)
}

export const getB64Svg = iconRef => {
  let src = ''
  if (iconRef) {
    // base64 encode
    const base64Svg = btoa(
      decodeURIComponent(encodeURIComponent(iconRef.$el.outerHTML)),
    )
    src = 'data:image/svg+xml;base64,' + base64Svg
  }
  return src
}

export const getB64Img = url => {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const reader = new FileReader()
        reader.readAsDataURL(blob)
        reader.onloadend = () => {
          const base64String = reader.result
          resolve(base64String)
        }
      })
      .catch(reject)
  })
}

export const getOS = () => {
  const userAgent = window.navigator.userAgent
  const platform =
    navigator?.userAgentData?.platform || navigator?.platform || 'unknown'

  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K']
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE']
  const iosPlatforms = ['iPhone', 'iPad', 'iPod']
  let os = null

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'Mac OS'
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS'
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows'
  } else if (/Android/.test(userAgent)) {
    os = 'Android'
  } else if (!os && /Linux/.test(platform)) {
    os = 'Linux'
  }

  return os
}

/**
 * RED-180 — GDPR-friendly address rendering for the broker-facing preview
 * and any other surface that must not expose precise locators.
 *
 * Mirrors `App\Services\AddressGeneralizationService::generalizeTransaction`
 * so the dialog preview matches the rendered .docx exactly:
 *  - Keeps `asukoht` (settlement), `ma_asum_linnaosa` (district), and
 *    `aadress` (street name) only when `maja_number` is present.
 *  - Drops `maja_number` (house number / cadaster name) and
 *    `korteri_number` (apartment).
 *
 * If the transaction has no usable components, returns null so callers can
 * fall back to whatever placeholder they like (the server returns the
 * 'Asukoht puudub' sentinel; in the preview we'd rather show '#<id>').
 *
 * @param {object|null|undefined} transactionDetail
 * @returns {string|null}
 */
export const generalizeTransactionAddress = transactionDetail => {
  if (!transactionDetail) return null

  const trim = v => (v == null ? '' : String(v).trim())

  const parts = []

  const settlement = trim(transactionDetail.asukoht)
  if (settlement !== '') parts.push(settlement)

  const district = trim(transactionDetail.ma_asum_linnaosa)
  if (district !== '') parts.push(district)

  const street = trim(transactionDetail.aadress)
  const hasHouseNumber = trim(transactionDetail.maja_number) !== ''
  if (street !== '' && hasHouseNumber) parts.push(street)

  return parts.length === 0 ? null : parts.join(', ')
}

export const isNumeric = n => {
  if (typeof n === 'string' && n.trim() === '') {
    return false
  }
  const no = typeof n === 'string' ? n.replace(',', '.') : n

  return typeof no === 'number' || (typeof no === 'string' && !isNaN(no))
}

export const selectContent = e => {
  const target = e?.target ?? e
  if (!target) return

  // Inputs and textareas: focus and move caret to the end (avoid select-all)
  const tag = (target.tagName || '').toLowerCase()
  if (tag === 'input' || tag === 'textarea') {
    if (target.disabled) {
      return
    }

    try {
      if (typeof target.focus === 'function') {
        try {
          target.focus({ preventScroll: true })
        } catch (_) {
          // Older browsers may not support options
          target.focus()
        }
      }

      const valueLength = typeof target.value === 'string' ? target.value.length : 0
      if (typeof target.setSelectionRange === 'function' && valueLength >= 0) {
        // Place the caret at the end
        target.setSelectionRange(valueLength, valueLength)
      }
      return
    } catch (_) {
      // fall through to generic selection as a last resort
    }
  }

  // Contenteditable elements: place caret at the end
  if (
    target.isContentEditable === true ||
    (typeof target.getAttribute === 'function' &&
      target.getAttribute('contenteditable') === 'true')
  ) {
    const selection = window.getSelection && window.getSelection()
    const range = document.createRange && document.createRange()
    if (selection && range) {
      range.selectNodeContents(target)
      range.collapse(false) // move to end
      selection.removeAllRanges()
      selection.addRange(range)
    }
    return
  }

  // Fallback: select node contents for non-form elements
  if (document.createRange) {
    const range = document.createRange()
    range.selectNodeContents(target)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  }
}

export const num = n => {
  if (typeof n === 'string') {
    return Number(n.replace(',', '.'))
  }
  return n
}

// RED-282: skip non-finite entries (missing keys, unparseable strings) the
// same way lodash `sum` already skips `undefined` — one absent `*_kohandus`
// field must not turn the whole kohanduse_abs cell into NaN.
export const sumArrayAbs = numbers => {
  return sum(
    numbers.map(n => {
      const v = num(n)
      return Number.isFinite(v) ? Math.abs(v) : 0
    }),
  )
}

export const arraymove = (arr, fromIndex, toIndex) => {
  const element = arr[fromIndex]
  arr.splice(fromIndex, 1)
  arr.splice(toIndex, 0, element)

  return arr
}

/**
 * Convert ML weights (0..1, keyed by transaction id) into integer percentages
 * that sum to the rounded total (≈100) using the largest-remainder method.
 *
 * Replaces per-row `Number.parseInt(weight * 100)`, which truncated every
 * weight (and floored sub-1% weights to 0), so the kohandustabel weighted
 * average summed less than 100% of the weight and fell below the page headline
 * (RED-225). Integer percents keep the table editable while summing correctly.
 *
 * @param {Record<string|number, number>} weights - id -> weight (0..1)
 * @param {Array<string|number>} [ids] - ids to allocate across (defaults to weight keys)
 * @returns {Record<string, number>} id -> integer percent
 */
export const weightsToPercentages = (weights, ids = null) => {
  const keys = (ids ?? Object.keys(weights ?? {})).map(String)
  const parts = keys.map(key => {
    const pct = (Number(weights?.[key]) || 0) * 100
    const base = Math.floor(pct)
    return { key, base, remainder: pct - base }
  })

  const target = Math.round(parts.reduce((sum, p) => sum + p.base + p.remainder, 0))
  let leftover = target - parts.reduce((sum, p) => sum + p.base, 0)

  // Hand the rounding leftover to the largest fractional remainders first.
  ;[...parts]
    .sort((a, b) => b.remainder - a.remainder)
    .forEach(part => {
      if (leftover > 0) {
        part.base += 1
        leftover -= 1
      }
    })

  return Object.fromEntries(parts.map(p => [p.key, p.base]))
}

/**
 * Per-comparable residual ("Määramata") adjustment — the leftover ML "Unknown"
 * bucket for legacy QuickValuation rows.
 *
 * The page headline (`display_price`, server-side WeightedAveragePriceCalculator)
 * sums EVERY numeric category in `differences.grouped` INCLUDING the model's
 * "Unknown" bucket; the kohandustabel sums only the named `*_kohandus` columns.
 * The gap between the two IS that bucket. Surfacing it as one explicit row makes
 * the table total reconcile to the already-stored `display_price` WITHOUT
 * re-running ML or mutating the persisted prediction.
 *
 *   residual = Σ(numeric raw `differences.grouped[id]`)  // incl. Unknown
 *            − Σ(numeric `grouped_differences[id]` keys ending in `_kohandus`)
 *
 * Returns 0 when no raw differences are present or the row is clean (no Unknown),
 * so the row stays hidden and totals are unchanged for post-fix predictions.
 *
 * @param {object} prediction - the prediction payload (savedPrediction / predict response)
 * @param {string|number} rowId - comparable transaction id
 * @returns {number} residual adjustment percentage (multiple of 5; 0 when clean)
 */
export const residualAdjustment = (prediction, rowId) => {
  const raw = prediction?.differences?.grouped?.[rowId]
  if (!raw || typeof raw !== 'object') {
    return 0
  }

  const sumAll = Object.values(raw).reduce(
    (sum, v) => sum + (typeof v === 'number' ? v : 0),
    0,
  )

  const named = prediction?.grouped_differences?.[rowId] ?? {}
  const sumNamed = Object.entries(named).reduce(
    (sum, [key, v]) =>
      sum + (key.endsWith('_kohandus') && typeof v === 'number' ? v : 0),
    0,
  )

  const residual = sumAll - sumNamed

  return Math.abs(residual) < 1e-9 ? 0 : Math.round(residual * 100) / 100
}
